import React from 'react';
import { styled } from '@linaria/react';
import { Pause, Play } from 'lucide-react';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useHasHydrated from '@/hooks/use-has-hydrated';

import Demo, { DemoSliderControl } from '@/components/Demo';
import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';
import Spinner from '@/components/Spinner';

function SquashStretchBall() {
  const [status, setStatus] = React.useState('paused');
  const hasHydrated = useHasHydrated();

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    setStatus(prefersReducedMotion ? 'paused' : 'running');
  }, [prefersReducedMotion]);

  return (
    <Demo
      outputWrapper={null}
      initialValues={{
        duration: 500,
        squashStretchAmount: 0,
      }}
      controlPosition="bottom"
      controls={[
        <DemoSliderControl
          key="squashStretchAmount"
          id="squashStretchAmount"
          label="Squash/Stretch Amount"
          min={0}
          max={1}
          step={0.01}
          previewRenderer={(value) => {
            return `${Math.round(Number(value) * 100)}%`;
          }}
        />,
      ]}
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
    >
      {({ duration, squashStretchAmount }) => {
        return (
          <Wrapper>
            <BallWrapper>
              <Bouncer
                style={{
                  '--bounce-duration': duration + 'ms',
                  animationPlayState: status,
                }}
              >
                <Ball
                  data-is-loading={!hasHydrated}
                  style={{
                    '--amount': squashStretchAmount,
                    animationPlayState: status,
                  }}
                >
                  {!hasHydrated && <Spinner color="white" />}
                </Ball>
              </Bouncer>
            </BallWrapper>
            <Button
              Icon={status === 'running' ? Pause : Play}
              discouraged={!hasHydrated}
              onClick={() => {
                setStatus(
                  status === 'running' ? 'paused' : 'running'
                );
              }}
            >
              {status === 'running' ? 'Pause' : 'Play'} Animation
            </Button>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 32px;
`;

const BallWrapper = styled.div`
  width: min(22rem, 100%);
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 16px;
  border-bottom: 4px solid var(--color-gray-700);
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
  outline-offset: 4px;
`;

const Bouncer = styled.div`
  --curve: cubic-bezier(0.123, 0.5, 0.5, 1);
  width: 50px;
  height: 50px;
  animation: bounce var(--bounce-duration) var(--curve) infinite
    alternate;
  will-change: transform;

  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    3% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-100px);
    }
  }
`;

const Ball = styled.div`
  display: grid;
  place-content: center;
  width: 100%;
  height: 100%;
  background: var(--wham-primary-500);
  border-radius: 50%;
  animation: squish calc(var(--bounce-duration) * 2) linear infinite;
  transition: background 300ms;
  transform-origin: center bottom;

  &[data-is-loading='true'] {
    opacity: 0.2;
  }
  @supports (
    background: hsl(from var(--wham-primary-500) h s l / 0.2)
  ) {
    &[data-is-loading='true'] {
      opacity: 1;
      background: hsl(from var(--wham-primary-500) h s l / 0.2);
    }
  }

  @keyframes squish {
    0% {
      transform: scale(
        calc(1 + var(--amount) * 0.75),
        calc(1 - var(--amount) * 0.75)
      );
    }
    10% {
      transform: scale(
        calc(-0.5 * var(--amount) + 1),
        calc(0.5 * var(--amount) + 1)
      );
    }
    100% {
      transform: scale(1, 1);
    }
  }
`;

const Button = styled(ButtonPrimary)`
  width: 100%;
  max-width: 20rem;
`;

export default SquashStretchBall;
